import FloatingInput from "@/components/ui/floatingInput";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "@/components/ui/form/form";
import SubmitButton from "@/components/ui/submit-button";
import Typography from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import useChangePin from "@/features/settings/useChangePin";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const pinSchema = z
	.object({
		currentPin: z
			.string()
			.length(4, { message: "PIN must be exactly 4 digits" })
			.regex(/^\d+$/, { message: "PIN must contain only numbers" }),
		newPin: z
			.string()
			.length(4, { message: "PIN must be exactly 4 digits" })
			.regex(/^\d+$/, { message: "PIN must contain only numbers" }),
		confirmPin: z
			.string()
			.length(4, { message: "PIN must be exactly 4 digits" })
			.regex(/^\d+$/, { message: "PIN must contain only numbers" }),
	})
	.refine((data) => data.newPin === data.confirmPin, {
		message: "PINs do not match",
		path: ["confirmPin"],
	});

type PinFormData = z.infer<typeof pinSchema>;

export default function ChangePinPage() {
	const changePinMutation = useChangePin();
	const { user } = useAuth();
	const { toast } = useToast();
	const form = useForm<PinFormData>({
		resolver: zodResolver(pinSchema),
		defaultValues: {
			currentPin: "",
			newPin: "",
			confirmPin: "",
		},
	});

	const onSubmit = async (data: PinFormData) => {
		changePinMutation.mutate(
			{
				guestId: user.id,
				payload: {
					current_pin: data.currentPin,
					new_pin: data.newPin,
				},
			},
			{
				onSuccess: () => {
					toast({
						title: "Success",
						description: "PIN has been changed successfully",
					});
					form.reset();
				},
				onError: (error) => {
					toast({
						title: "Error",
						description: error.message,
						variant: "destructive",
					});
				},
			},
		);
	};

	return (
		<div className="container mx-auto max-w-md">
			<div className="flex items-center mb-6">
				<Typography variant={"h5"}>Change 4-digit PIN</Typography>
			</div>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
					<FormField
						control={form.control}
						name="currentPin"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<FloatingInput
										label="Current PIN"
										type="password"
										maxLength={4}
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="newPin"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<FloatingInput
										label="New PIN "
										type="password"
										maxLength={4}
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="confirmPin"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<FloatingInput
										label="Confirm New PIN"
										type="password"
										maxLength={4}
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<SubmitButton
						className="w-full"
						mutationStatus={changePinMutation.status}
					>
						Submit
					</SubmitButton>
				</form>
			</Form>
		</div>
	);
}
